import { useEffect, useState } from 'react';

import { User, useLoginUserViaOAuth } from '@givebacks/api/core';
import { getErrorMessage_v2 } from 'core/Utils';
import { useSetAtom } from 'jotai';
import { Center, LoadingOverlay } from 'ui';

import { resetOnboardingAtoms } from '../../atoms/onboarding';
import { useOnboardingReferrals } from './hooks';

declare global {
  interface Window {
    google: any;
    onGoogleLibraryLoad: any;
  }
}

interface GoogleSignInRequest {
  token?: string;
  cause?: string;
  signup_referral_code?: string;
}

const GoogleSignIn = ({
  setThirdPartyAccountError,
  onSuccess,
}: {
  setThirdPartyAccountError: (str: string) => void;
  onSuccess: (user: User) => void;
}) => {
  const [googleLibraryLoaded, setGoogleLibraryLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const { referredCauseId, referralCode } = useOnboardingReferrals();
  const { mutate: loginUserViaOAuth } = useLoginUserViaOAuth();
  const clearOnboardingCodes = useSetAtom(resetOnboardingAtoms);

  const handleSignIn = async (response: { credential: string }) => {
    setLoading(true);
    const bodyParams: GoogleSignInRequest = {
      token: response.credential,
    };
    if (referredCauseId) {
      Object.assign(bodyParams, { cause: referredCauseId });
    }
    if (referralCode) {
      Object.assign(bodyParams, { signup_referral_code: referralCode });
    }

    loginUserViaOAuth(
      { provider: 'google_jwt', bodyParams },
      {
        onSuccess: ({ user }) => {
          clearOnboardingCodes();
          onSuccess(user);
        },
        onError: (error: Error) => {
          let errorMessage = 'Error: Could not sign in with Google';
          try {
            errorMessage = getErrorMessage_v2(error, 'Error: Could not sign in with Google');
          } catch {}
          setThirdPartyAccountError(errorMessage);
        },
        onSettled: () => {
          setLoading(false);
        },
      }
    );
  };

  useEffect(() => {
    const googleLoginDiv: HTMLElement = document.getElementById('sign_in_with_google')!;
    const googleLoginParentDiv: HTMLElement = document.getElementById(
      'sign_in_with_google_container'
    )!;

    if (window.google && window.google?.accounts) {
      window.google.accounts.id.initialize({
        client_id: '488598739330-5h4inccsvrclgm435g10fmm8831sls0r.apps.googleusercontent.com',
        callback: handleSignIn,
      });

      window.google.accounts.id.renderButton(googleLoginDiv, {
        type: 'standard',
        theme: 'filled_blue',
        width: googleLoginParentDiv.offsetWidth,
        text: 'signin_with',
      });
    }
  }, [googleLibraryLoaded]);

  useEffect(() => {
    window.onGoogleLibraryLoad = () => {
      setGoogleLibraryLoaded(true);
    };
  }, []);

  return (
    <Center id="sign_in_with_google_container">
      <button id="sign_in_with_google" style={{}} />
      <LoadingOverlay visible={loading} />
    </Center>
  );
};

export default GoogleSignIn;
